import { Component, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewChecked } from '@angular/core';
import { ChatNotificationService } from '../services/chat-notification.service';
import { WhatsAppChatService, WhatsAppMessage } from '../services/whatsapp-chat.service';
import { Subscription } from 'rxjs';
import { Router } from '@angular/router';

export interface ChatMessage {
  id: number;
  customer_wa_id: string;
  customer_phone?: string;
  customer_username?: string;
  message_text: string;
  media_file_path?: string;
  media_type?: string;
  media_mime_type?: string;
  message_type?: string;
  contact_data?: string;
  location_data?: string;
  received_at: string;
  is_read: string;
  salon_id: number;
}

@Component({
  selector: 'app-whatsapp-chat',
  templateUrl: './whatsapp-chat.component.html',
  styleUrls: ['./whatsapp-chat.component.css']
})
export class WhatsappChatComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('messagesContainer') messagesContainer: ElementRef;

  conversations: any[] = [];
  selectedConversation: any = null;
  messages: ChatMessage[] = [];
  newMessage: string = '';
  currentUser: any;
  private subscription: Subscription = new Subscription();
  private shouldScrollToBottom = false;
  private isFirstLoad = true;
  private lastScrollHeight = 0;

  // Modal properties
  showMediaModal = false;
  modalMediaUrl = '';
  modalMediaType = '';

  constructor(
    private router: Router,
    private chatService: ChatNotificationService,
    private whatsappService: WhatsAppChatService
  ) {
    this.currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
  }

  ngOnInit() {
    if (this.shouldPoll()) {
      this.loadConversations();
      this.subscribeToMessages();
    } else {
      this.router.navigate(['/Selectport']);
      return;
    }

  }

  private shouldPoll(): boolean {
    return this.currentUser.salon_id &&
      this.currentUser.whatsAppEnabled === '1' &&
      this.currentUser.incomingMessage === '1';
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  loadConversations() {
    this.whatsappService.getConversations().subscribe(
      conversations => {
        this.conversations = conversations;
      }
    );
  }

  selectConversation(conversation: any) {
    this.selectedConversation = conversation;
    this.isFirstLoad = true;
    this.shouldScrollToBottom = true;
    this.loadMessages(conversation.customer_wa_id);
    this.markAsRead(conversation.customer_wa_id);
  }

  loadMessages(customerId: string) {
    this.whatsappService.getConversationMessages(customerId).subscribe(
      messages => {
        const wasAtBottom = this.isScrolledToBottom();
        this.messages = messages;

        if (this.isFirstLoad || wasAtBottom) {
          this.shouldScrollToBottom = true;
        }
        this.isFirstLoad = false;
      }
    );
  }

  sendMessage() {
    if (!this.newMessage.trim() || !this.selectedConversation) return;

    const now = new Date();
    const canReplyUntil = new Date(this.selectedConversation.last_message_time);
    canReplyUntil.setHours(canReplyUntil.getHours() + 8);

    if (now > canReplyUntil) {
      alert('Cannot reply after 8 hours from last customer message');
      return;
    }

    this.whatsappService.sendMessage(
      this.selectedConversation.customer_wa_id,
      this.newMessage
    ).subscribe(
      response => {
        if (response && response.status === 'error') {
          alert(response.message);
          return;
        }
        this.newMessage = '';
        this.shouldScrollToBottom = true;
        this.loadMessages(this.selectedConversation.customer_wa_id);
        this.loadConversations();
      }
    );
  }

  markAsRead(customerId: string) {
    this.whatsappService.markConversationAsRead(customerId).subscribe();
  }

  canReply(): boolean {
    if (!this.selectedConversation) return false;

    const now = new Date();
    const canReplyUntil = new Date(this.selectedConversation.last_message_time);
    canReplyUntil.setHours(canReplyUntil.getHours() + 8);

    return now <= canReplyUntil;
  }

  private subscribeToMessages() {
    // Subscribe to the service's message stream
    this.subscription.add(
      this.whatsappService.messages$.subscribe(messages => {
        if (messages.length > 0) {
          this.loadConversations();
          if (this.selectedConversation) {
            const wasAtBottom = this.isScrolledToBottom();
            this.loadMessages(this.selectedConversation.customer_wa_id);
            if (wasAtBottom) {
              this.shouldScrollToBottom = true;
            }
          }
        }
      })
    );

    this.subscription.add(
      this.chatService.notification$.subscribe(notification => {
        if (notification.unreadCount > 0) {
          this.loadConversations();
        }
      })
    );
  }

  formatTime(dateString: string): string {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  formatDate(dateString: string): string {
    const messageDate = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (messageDate.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (messageDate.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return messageDate.toLocaleDateString('en-US', {
        day: 'numeric',
        month: 'short',
        year: messageDate.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
      });
    }
  }

  shouldShowDateSeparator(currentMessage: ChatMessage, previousMessage: ChatMessage | null): boolean {
    if (!previousMessage) return true;

    const currentDate = new Date(currentMessage.received_at).toDateString();
    const previousDate = new Date(previousMessage.received_at).toDateString();

    return currentDate !== previousDate;
  }

  ngAfterViewChecked() {
    if (this.shouldScrollToBottom && this.messagesContainer) {
      this.scrollToBottom();
      this.shouldScrollToBottom = false;
    }
  }

  private scrollToBottom(): void {
    try {
      const element = this.messagesContainer.nativeElement;
      element.scrollTop = element.scrollHeight;
      this.lastScrollHeight = element.scrollHeight;
    } catch (err) { }
  }

  private isScrolledToBottom(): boolean {
    if (!this.messagesContainer) return true;
    const element = this.messagesContainer.nativeElement;
    return element.scrollTop + element.clientHeight >= element.scrollHeight - 50;
  }

  openMediaModal(mediaUrl: string, mediaType: string) {
    this.modalMediaUrl = mediaUrl;
    this.modalMediaType = mediaType;
    this.showMediaModal = true;
  }

  closeMediaModal() {
    this.showMediaModal = false;
    this.modalMediaUrl = '';
    this.modalMediaType = '';
  }

  downloadMedia(url: string) {
    const link = document.createElement('a');
    link.href = url;
    link.download = url.split('/').pop() || 'download';
    link.click();
  }

  getContactInfo(contactData: string): any {
    try {
      return JSON.parse(contactData);
    } catch (e) {
      return null;
    }
  }

  getLocationInfo(locationData: string): any {
    try {
      return JSON.parse(locationData);
    } catch (e) {
      return null;
    }
  }

  openLocationMap(latitude: number, longitude: number) {
    const url = `https://www.google.com/maps?q=${latitude},${longitude}`;
    window.open(url, '_blank');
  }

  getMediaType(filePath: string, mimeType?: string): string {
    if (!filePath) return '';

    // First check MIME type if available
    if (mimeType) {
      if (mimeType.startsWith('image/')) return 'image';
      if (mimeType.startsWith('video/')) return 'video';
      if (mimeType.startsWith('audio/')) return 'audio';
      if (mimeType.includes('pdf') || mimeType.includes('document') || mimeType.includes('word') ||
        mimeType.includes('excel') || mimeType.includes('powerpoint') || mimeType.includes('sql') ||
        mimeType.includes('text/plain') || mimeType.includes('zip') || mimeType.includes('rar') ||
        mimeType.includes('debian') || mimeType.includes('executable') || mimeType.includes('android') ||
        mimeType.includes('gzip') || mimeType.includes('tar') || mimeType.includes('html') ||
        mimeType.includes('css') || mimeType.includes('php') || mimeType.includes('python') ||
        mimeType.includes('x509') || mimeType.includes('oasis')) return 'document';
    }

    // Fallback to file extension
    const parts = filePath.split('.');
    const ext = parts.length > 1 ? parts.pop().toLowerCase() : '';

    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext || '')) return 'image';
    if (['mp4', 'avi', 'mov', 'webm'].includes(ext || '')) return 'video';
    if (['mp3', 'ogg', 'wav', 'mpeg', 'm4a', 'aac', 'bin'].includes(ext || '')) return 'audio';
    if (['pdf', 'doc', 'docx', 'txt', 'xls', 'xlsx', 'ods', 'ppt', 'pptx', 'sql', 'zip', 'rar', 'deb', 'exe', 'apk', 'gz', 'tar', 'html', 'css', 'php', 'ts', 'phtml', 'py', 'pem', 'ppk', 'pub', 'obd', 'web'].includes(ext || '')) return 'document';

    // Handle .tar.gz files
    if (filePath.endsWith('.tar.gz')) return 'document';

    // If no extension, assume it's an audio file (common for WhatsApp voice messages)
    return ext ? 'document' : 'audio'; // Changed default to 'document' for unknown files
  }
}


// WEBPACK FOOTER //
// ./src/app/whatsapp-chat/whatsapp-chat.component.ts